/**
 * Domain data entities — projects, contacts, routines, and calendar events.
 * Pure data shapes shared by API routes, storage, and MCP tools. No config.
 */
import type { ChannelExecutor } from './channels.js';

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export interface ProjectEntry {
  id: string;
  project: string;
  description: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  /** Last time this entry was returned by retrieval (R6). Read-only sidecar field. */
  accessedAt?: string | undefined;
  /** Cumulative retrieval-hit count from the FTS sidecar. Read-only. */
  hitCount?: number | undefined;
  /**
   * True when the entry lives under `<project>/knowledge/archive/` (Option A:
   * archived entries stay searchable, tagged historical). Derived from the file
   * path at read time; lets `rebuildFtsIndex` reconstruct the `project/archive`
   * FTS folder so a rebuild matches the live index instead of dropping archived
   * rows.
   */
  archived?: boolean | undefined;
  /**
   * ASI06 provenance — who created this
   * entry — surfaced at recall so routine-authored project knowledge is
   * distinguishable from operator-trusted entries. Pre-H5 entries carry
   * `source: undefined`; the recall formatter treats absent as `unknown`.
   */
  source?: 'human' | 'agent' | 'routine' | 'external' | undefined;
  /**
   * Trust tier (H5). Determines recall heuristics — `trusted` (operator-authored),
   * `derived` (agent-generated), or `external` (ingested from outside).
   */
  trust?: 'trusted' | 'derived' | 'external' | undefined;
  /**
   * Provenance backlink (2026-07-01) — the conversation ID this entry was saved
   * from. Lets recall route back to the source discussion. Absent on routine/migration saves.
   */
  sourceConversation?: string | undefined;
  /** Routine ID that produced this entry. */
  sourceRoutine?: string | undefined;
  /** Human-readable routine label for provenance display. */
  sourceRoutineLabel?: string | undefined;
  /** Primary source URL(s) that informed this entry. */
  sourceUrl?: string | undefined;
  /**
   * Optional shallow sub-folder under the project's `knowledge/` root, derived
   * from the entry's physical path (single source of truth — NOT stored in
   * frontmatter). Undefined = knowledge root. Example: `routines/kalshi-brief`.
   * Depth-capped at 2 levels; `archive` reserved.
   */
  folder?: string | undefined;
}

export interface CreateProjectInput {
  project: string;
  content: string;
  description?: string | undefined;
  /**
   * Optional shallow sub-folder under `knowledge/` (e.g. `routines/<slug>`).
   * Validated + normalized by `sanitizeKnowledgeFolder` — lowercase kebab-case,
   * max 2 levels, `archive` reserved. Omit for the knowledge root.
   */
  folder?: string | undefined;
  /** ASI06 provenance — defaults to 'agent' on save. */
  source?: 'human' | 'agent' | 'routine' | 'external' | undefined;
  /** ASI06 trust tier — defaults to 'derived' on save. */
  trust?: 'trusted' | 'derived' | 'external' | undefined;
  /** Provenance backlink — see `ProjectEntry.sourceConversation`. Handler-supplied. */
  sourceConversation?: string | undefined;
  /** Routine provenance — see `ProjectEntry.sourceRoutine`. */
  sourceRoutine?: string | undefined;
  /** Human-readable routine label — see `ProjectEntry.sourceRoutineLabel`. */
  sourceRoutineLabel?: string | undefined;
  /** Primary source URL(s) — see `ProjectEntry.sourceUrl`. */
  sourceUrl?: string | undefined;
}

export interface UpdateProjectInput {
  content?: string | undefined;
  /** New label; `""` re-derives it from the content (as on save). */
  description?: string | undefined;
  /**
   * ASI06 provenance — re-stamped on edit. A dashboard (operator) edit passes
   * `source: 'human'` so the agent sees at recall that a human took ownership
   * of the entry (bidirectional awareness — neither side edits in the dark).
   */
  source?: 'human' | 'agent' | 'routine' | 'external' | undefined;
  /** ASI06 trust tier — re-stamped on edit alongside `source`. */
  trust?: 'trusted' | 'derived' | 'external' | undefined;
}

/**
 * Per-project structured metadata stored at `~/.onestone/projects/<name>/meta.json`.
 *
 * The record's existence is what makes a project exist (the registry, MA-390).
 * Its fields carry what the prompt builder needs and knowledge entries don't:
 * what the project is, where its code lives, which skills to surface, which
 * projects it works with. Every field is optional. Changes go through
 * `ProjectDetailsPatchSchema` (projectDetails.ts) and `ProjectStoreCore.updateMeta`.
 */
export interface ProjectMeta {
  /** One-line project description shown in the situation block. Max 200 chars. */
  description?: string;
  /**
   * Absolute filesystem path of the project's source code. When set AND the
   * path is a git repo, the situation block emits `## Current Working Context`
   * with branch + `git status --short` + last error from today's server log.
   */
  cwd?: string;
  /**
   * Skill allowlist for this project. When set, the system-prompt skill menu
   * is filtered to these names — saves tokens and reduces wrong-skill loads.
   * Unset → full catalog appears.
   */
  skills?: string[];
  /**
   * Whether the per-turn note carries git branch + working tree (MA-282).
   * 'primary' — branch + working tree shown.
   * 'none' — skip git entirely, cwd only.
   * Unset defaults to 'none'.
   */
  vcsGrounding?: VcsGrounding;
  /**
   * Projects this one works with (e.g. a game and its asset library). They
   * are always in view from this project: readable, and bash/quality tools
   * may run in them, without switching. Writes still need a switch.
   */
  related?: string[];
  /**
   * Paths in the code folder (relative, `/`-separated — a folder or a file)
   * whose changes the trash does not keep: things another program rewrites
   * constantly, such as a bot's state. On top of Settings' skipped folder names.
   */
  trashSkip?: string[];
}

/** MA-282 — project state-signal modes for git grounding in the situation block. */
export const VCS_GROUNDING_MODES = ['primary', 'none'] as const;
export type VcsGrounding = (typeof VCS_GROUNDING_MODES)[number];

/** Project names: lowercase kebab-case alphanumeric. */
export const PROJECT_NAME_RE = /^[a-z0-9][a-z0-9-]*$/;

// ---------------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------------

export const CONTACT_TYPES = ['client', 'friend', 'vendor', 'other'] as const;
export type ContactType = (typeof CONTACT_TYPES)[number];

export interface ContactEntry {
  id: string;
  name: string;
  contactType: ContactType;
  title: string;
  emails: string[];
  phone: string;
  company: string;
  location: string;
  website: string;
  /** The projects this contact belongs to — at least one (General when none was chosen). */
  projects: string[];
  tags: string[];
  linkedContactId: string;
  linkType: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateContactInput {
  name: string;
  contactType?: ContactType | undefined;
  title?: string | undefined;
  emails?: string[] | undefined;
  email?: string | undefined;
  phone?: string | undefined;
  company?: string | undefined;
  location?: string | undefined;
  website?: string | undefined;
  /** At least one project (see ContactEntry.projects). */
  projects: string[];
  tags?: string[] | undefined;
  linkedContactId?: string | undefined;
  linkType?: string | undefined;
  notes?: string | undefined;
}

/** Same fields as create, with `name` also optional (explicit undefined allowed). */
export type UpdateContactInput = {
  [K in keyof CreateContactInput]?: CreateContactInput[K] | undefined;
};

// ---------------------------------------------------------------------------
// Routines
// ---------------------------------------------------------------------------

// Note: routine outputs ALWAYS upsert to project knowledge (MA-323, 2026-08-05).
// A bound routine writes to its project's `knowledge/routines/<slug>/`; an
// unbound routine writes to the `general` project's `knowledge/routines/<slug>/`.
// The entry is keyed by the producing routine (`sourceRoutine`) and UPDATED
// IN PLACE each run — never appended — so a routine has exactly one persistent,
// predictable, indexed output location. The `OutputTarget`/`outputKind`
// discriminated union that previously toggled this behavior was deleted:
// its `none` variant trapped 10KB-capped output text in run history where
// no search surface could reach it, and its `save` variant did what every
// routine should always do. See executor.ts:upsertRoutineOutput.

export interface RoutineSpec {
  id: string;
  label: string;
  cron: string;
  timezone: string;
  prompt: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  category?: string;
  /**
   * The project this routine belongs to (General when none was chosen —
   * `../general.ts`). The routine engine runs it AT the project: its working
   * directory is the project's `meta.cwd` and its output is saved there.
   */
  project: string;
  /**
   * Per-routine model override. When set, the routine fires with this model
   * instead of the channel-level "routine" default from Settings → Model.
   * Accepts any model id from any configured provider (e.g. "claude-sonnet-4-20250514",
   * "gemini-2.5-flash"). Unset → uses the channel default.
   */
  model?: string;
  /**
   * ISO-8601 UTC timestamp
   * of the last time a reviewer (operator via the dashboard, or an audit
   * routine via `routine_update`) confirmed this routine's config/output is
   * still good. Surfaces "which routines are stale/unchecked" across the
   * portfolio. Unset → never reviewed. Set through the ONE update path
   * (`PUT /api/routines/:id` + `routine_update`), same as every other field —
   * no separate review endpoint. `reviewedBy` is intentionally NOT stored:
   * single-operator app, so it would be a constant carrying zero signal.
   */
  reviewedAt?: string;
  /**
   * When true, the routine
   * is flagged as a candidate for removal (low value, superseded, chronically
   * failing) — a soft triage signal that sits between `enabled: false` and
   * outright delete. Surfaces in the dashboard for a cleanup pass. Set by the
   * operator (toggle) or an audit routine (`routine_update`). Unset/false →
   * not a candidate; only persisted when true.
   */
  archiveCandidate?: boolean;
  /**
   * Positive-security tool allowlist (OWASP Agentic #5, Anthropic Zero Trust).
   * When set, only these MCP tool names are available to the routine — all
   * others are excluded. On the CLI path, passed as `--allowedTools`; on the
   * SDK path, the tool definitions array is filtered before entering the loop.
   * Unset → all tools available.
   * Empty array is rejected at validation (a toolless agent is useless).
   */
  allowedTools?: string[];
}

// `?: T | undefined` is deliberate (matches CreateCalendarInput / CreateContactInput
// style) — under `exactOptionalPropertyTypes: true`, this widens the field to
// accept explicit `undefined`, which JSON deserialization can produce. Keeps
// the routine-input surface consistent with the other input types and lets
// the runtime schemas in `shared/schemas.ts` prove bidirectional type parity.
export interface CreateRoutineInput {
  label: string;
  cron: string;
  timezone?: string | undefined;
  prompt: string;
  enabled?: boolean | undefined;
  id?: string | undefined;
  category?: string | undefined;
  /** The routine's project — required (see RoutineSpec.project). */
  project: string;
  /** Per-routine model override — see RoutineSpec.model. */
  model?: string | undefined;
  /** Positive-security tool allowlist — see RoutineSpec.allowedTools. */
  allowedTools?: string[] | undefined;
}

export interface UpdateRoutineInput {
  label?: string | undefined;
  cron?: string | undefined;
  timezone?: string | undefined;
  prompt?: string | undefined;
  enabled?: boolean | undefined;
  /** Empty string clears. */
  category?: string | undefined;
  /** Move the routine to another project. A routine always has one, so it can't be cleared. */
  project?: string | undefined;
  /** Per-routine model override — see RoutineSpec.model. Empty/null clears. */
  model?: string | null | undefined;
  /**
   * Review timestamp — see RoutineSpec.reviewedAt. A non-empty ISO string sets
   * it (typically `now`); `null` or empty string explicitly clears (un-review);
   * undefined = no change. Mirrors the model clear-vs-keep pattern.
   */
  reviewedAt?: string | null | undefined;
  /**
   * Archive-candidate flag — see RoutineSpec.archiveCandidate. `true` flags,
   * `false` clears; undefined = no change.
   */
  archiveCandidate?: boolean | undefined;
  /**
   * Positive-security tool allowlist — see RoutineSpec.allowedTools.
   * Non-empty array sets; `null` or empty array clears (reverts to all tools);
   * undefined = no change.
   */
  allowedTools?: string[] | null | undefined;
}

/**
 * Per-routine run audit record — produced by the routine executor, persisted
 * as JSONL, served by /api/routines, rendered in the dashboard run history.
 * Metering fields are optional: records written before token metering lack them.
 */
export interface RoutineRunResult {
  routineId: string;
  startedAt: string;
  completedAt: string;
  status: 'success' | 'error';
  output: string;
  error: string | null;
  inputTokens?: number;
  outputTokens?: number;
  cost?: number;
  model?: string;
  /** Agent loop turns (assistant responses) — NOT tool calls. */
  turns?: number;
  /** Tool calls dispatched during the run. */
  toolCallCount?: number;
  /** Which engine ran the turn — cli (subscription) or sdk (API key). */
  executor?: ChannelExecutor;
  /** The run's id — its row id in the run history, and the `runId` its log
   *  lines, ops events and tool calls carry. */
  runId: string;
  /**
   * Billing regime — 'subscription' when the run went through the Claude Code
   * subscription (CLI executor); 'api' when it hit paid API tokens. Under
   * 'subscription' the `cost` field is HYPOTHETICAL — what an equivalent
   * API-billed run would cost — not real out-of-pocket spend. Aggregators use
   * this to split hypothetical spend from real. */
  billed?: 'subscription' | 'api';
  /** Per-model token breakdown when the executor can distinguish (CLI parses
   *  `result.modelUsage`; SDK path emits a single entry). Ground truth for
   *  attribution — Claude Code auto-routes internally and the flat model tag
   *  above is only what the caller REQUESTED. Kept as `unknown` here to avoid
   *  a cross-package type dependency; the emit site serializes shape-checked. */
  modelUsage?: Array<Record<string, unknown>>;
  /** Anthropic's canonical top-line cost from `result.total_cost_usd`. Preferred
   *  over `cost` when present (matches what `/usage` in the TUI shows). */
  totalCostUsd?: number;
}

// ---------------------------------------------------------------------------
// Calendar — point-in-time events (deadlines, meetings, milestones)
// ---------------------------------------------------------------------------

export const CALENDAR_EVENT_TYPES = ['deadline', 'milestone', 'reminder', 'meeting'] as const;
export type CalendarEventType = (typeof CALENDAR_EVENT_TYPES)[number];

export interface CalendarEntry {
  id: string;
  title: string;
  description: string;
  eventDate: string;
  eventType: CalendarEventType;
  /** The project this event belongs to (General when none was chosen — `../general.ts`). */
  project: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCalendarInput {
  title: string;
  eventDate: string;
  description?: string | undefined;
  eventType?: CalendarEventType | undefined;
  /** The event's project — required (see CalendarEntry.project). */
  project: string;
}

export interface UpdateCalendarInput {
  title?: string | undefined;
  description?: string | undefined;
  eventDate?: string | undefined;
  eventType?: CalendarEventType | undefined;
  /** Move the event to another project. Every event has one, so it can't be cleared. */
  project?: string | undefined;
}
